import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Tooltip,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import BarChartIcon from '@mui/icons-material/BarChart';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ProfilingDialog from './dialogs/ProfilingDialog';

const TargetPanel = ({ 
  targetSchema = [],
  disabled = false,
  targetProfileData = [],
}) => {
  const [expandedValues, setExpandedValues] = useState({});
  const [sampleExpandedMap, setSampleExpandedMap] = useState({});
  const [profilingExpandedMap, setProfilingExpandedMap] = useState({}); // NEW
  const [rowExpandedMap, setRowExpandedMap] = useState({});
  const [expandedMap, setExpandedMap] = useState({});
  const [profilingDialogOpen, setProfilingDialogOpen] = useState(false);
  const [selectedProfileData, setSelectedProfileData] = useState(null);
  const [selectedTargetName, setSelectedTargetName] = useState('');

  const toggleTargetSchema = (targetId) => {
    if (disabled) return;
    setExpandedMap((prev) => ({
      ...prev,
      [targetId]: !prev[targetId],
    }));
  };

  const toggleSampleExpanded = (e, sourceKey) => {
    e.stopPropagation();
    setSampleExpandedMap((prev) => ({ ...prev, [sourceKey]: !prev[sourceKey] }));
  };

  // NEW: Toggle profiling section
  const toggleProfilingExpanded = (e, sourceKey) => {
    e.stopPropagation();
    setProfilingExpandedMap((prev) => ({ ...prev, [sourceKey]: !prev[sourceKey] }));
  };

  const toggleRowExpanded = (e, rowKey) => {
    e.stopPropagation();
    setRowExpandedMap((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const toggleValueExpand = (sourceId, rowIdx, colKey) => {
    const key = `${sourceId}-${rowIdx}-${colKey}`;
    setExpandedValues((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusIcon = (target) => {
    if (target.sampleRows?.length > 0)
      return <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50', ml: 1 }} />;
    if (target.error)
      return <ErrorIcon fontSize="small" sx={{ color: '#f44336', ml: 1 }} />;
    return null;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    const str = String(text);
    return str.length <= maxLength ? str : `${str.substring(0, maxLength)}...`;
  };

  const renderValue = (value, sourceId, rowIdx, colKey) => {
    if (value === null || value === undefined)
      return <Box component="span" sx={{ color: '#aaa', fontStyle: 'italic' }}>—</Box>;

    const str = String(value);
    const expandKey = `${sourceId}-${rowIdx}-${colKey}`;
    const isExpanded = expandedValues[expandKey];

    if (str.length > 30) {
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            toggleValueExpand(sourceId, rowIdx, colKey);
          }}
          sx={{
            cursor: 'pointer',
            color: '#2196f3',
            textDecoration: 'underline dotted',
            '&:hover': { color: '#1976d2' },
          }}
        >
          {isExpanded ? str : truncateText(str)}
        </Box>
      );
    }
    return str;
  };

  const getProfileForTarget = (targetName) => {
    return targetProfileData?.find(p => p.destination_name === targetName);
  };

  const openProfilingDialog = (e, targetName) => {
    e.stopPropagation();
    const profile = getProfileForTarget(targetName);
    setSelectedProfileData(profile);
    setSelectedTargetName(targetName);
    setProfilingDialogOpen(true);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        width: 360,
        height: 700,
        overflow: 'auto',
        p: 2,
        border: '1px solid #e5e7eb',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        userSelect: disabled ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Panel Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Target Schema
        </Typography>
        <Chip
          size="small"
          label={`${targetSchema.length} ${targetSchema.length === 1 ? 'target' : 'targets'}`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Target Schema Cards */}
      {/* Target Schema Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {targetSchema.length > 0 ? (
          targetSchema.map((target) => {
            const sourceKey = target.id || `target-${target.nodeId || Math.random()}`;
            const columnCount = target.columns?.length ?? 0;
            const sampleOpen = !!sampleExpandedMap[sourceKey];
            const profilingOpen = !!profilingExpandedMap[sourceKey];
            const hasSamples = target.sampleRows?.length > 0;
            const schemaName = target.tableName || 'Target Schema';
            const profileData = getProfileForTarget(schemaName);
            const hasProfile = !!profileData;

            return (
              <Paper
                key={sourceKey}
                elevation={0}
                sx={{
                  bgcolor: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  p: 2,
                  opacity: target.error ? 0.7 : 1,
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: disabled ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {/* ── Target Schema row header ── */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                  onClick={() => toggleTargetSchema(target.id)}
                >
                  {/* Left: name + status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                    <Tooltip title={schemaName}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 180,
                          color: '#1f2937',
                        }}
                      >
                        {schemaName}
                      </Typography>
                    </Tooltip>
                    {getStatusIcon(target)}
                  </Box>

                  {/* Right: blue column count chip + chevron */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Tooltip title={`${columnCount} columns`}>
                      <Chip
                        size="small"
                        icon={<ViewColumnIcon sx={{ fontSize: '13px !important', color: 'white !important' }} />}
                        label={columnCount}
                        className="button-color"
                        sx={{
                          height: 22,
                          px: 0.25,
                          fontSize: '0.72rem',
                          background: '#1976D2',
                          fontWeight: 700,
                          color: 'white',
                          '& .MuiChip-icon': { ml: '4px', color: 'white' },
                          '& .MuiChip-label': { pr: '6px', color: 'white' },
                        }}
                      />
                    </Tooltip>
                    <IconButton size="small" disabled={disabled} sx={{ p: 0.25 }}>
                      {expandedMap[target.id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </IconButton>
                  </Box>
                </Box>

                {/* ── Expanded body ── */}
                <Collapse in={!!expandedMap[target.id]} timeout={220}>
                  <Box sx={{ mt: 1.5 }}>
                    {/* Column list */}
                    <Box
                      sx={{
                        maxHeight: 150,
                        overflow: 'auto',
                        p: 1,
                        bgcolor: '#ffffff',
                        borderRadius: 1,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      {target.columns && target.columns.length > 0 ? (
                        target.columns.map((column, idx) => {
                          const columnName =
                            column.column_name || column.name || (typeof column === 'string' ? column : '');
                          const dataType = column.data_type;
                          const displayText = dataType ? `${columnName} (${dataType})` : columnName;
                          const columnKey = `${sourceKey}-col-${idx}`;

                          return (
                            <Tooltip key={columnKey} title={displayText} placement="right">
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  fontFamily: 'monospace',
                                  fontSize: '0.7rem',
                                  color: '#374151',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  py: 0.25,
                                }}
                              >
                                • {truncateText(displayText, 50)}
                              </Typography>
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                          No columns available
                        </Typography>
                      )}
                    </Box>

                    {/* ── Data Profiling Section (COLLAPSIBLE) ── */}
                    {hasProfile && (
                      <Box sx={{ mt: 1.5 }}>
                        {/* Profiling toggle bar - CHANGED: Now clickable with arrow */}
                        <Box
                          onClick={(e) => toggleProfilingExpanded(e, sourceKey)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            border: '1px solid #e0e7ff',
                            transition: 'background 0.18s',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <BarChartIcon sx={{ fontSize: 14, color: '#1f2937' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937', lineHeight: 1 }}>
                              Data Profiling
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {/* Open full dialog button */}
                            <Tooltip title="View detailed profiling">
                              <IconButton
                                size="small"
                                onClick={(e) => openProfilingDialog(e, schemaName)}
                                sx={{
                                  p: 0.5,
                                  bgcolor: '#1976d2',
                                  color: 'white',
                                  '&:hover': { bgcolor: '#1976d2' },
                                }}
                              >
                                <OpenInFullIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                            {/* Collapse/expand arrow */}
                            <IconButton
                              size="small"
                              sx={{
                                p: 0,
                                transition: 'transform 0.2s',
                                transform: profilingOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            >
                              <ExpandMoreIcon sx={{ fontSize: 16, color: '#1f2937' }} />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Collapsible Inline Quick Stats */}
                        <Collapse in={profilingOpen} timeout={220}>
                          <Box
                            sx={{
                              mt: 0.75,
                              p: 1.5,
                              bgcolor: '#ffffff',
                              borderRadius: 1,
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: '#6b7280', display: 'block', mb: 1 }}
                            >
                              Quick Stats (Top 3 Columns)
                            </Typography>
                            {profileData.columns?.slice(0, 3).map((col, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 0.5,
                                  borderBottom: idx < 2 ? '1px dashed #e5e7eb' : 'none',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    color: '#374151',
                                  }}
                                >
                                  {truncateText(col.column_name, 15)}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Chip
                                    label={col.data_type}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.6rem',
                                      bgcolor: '#f3f4f6',
                                      fontFamily: 'monospace',
                                    }}
                                  />
                                  <Chip
                                    label={`∅ ${col.null_count}`}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.6rem',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Chip
                                    label={`≠ ${col.distinct_count}`}
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.6rem',
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                              </Box>
                            ))}
                            {profileData.columns?.length > 3 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  textAlign: 'center',
                                  mt: 1,
                                  color: '#6b7280',
                                  fontStyle: 'italic',
                                }}
                              >
                                +{profileData.columns.length - 3} more columns
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* ── Sample Data section ── */}
                    {hasSamples && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          onClick={(e) => toggleSampleExpanded(e, sourceKey)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            transition: 'background 0.18s',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <TableRowsIcon sx={{ fontSize: 14, color: '#1f2937' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1f2937', lineHeight: 1 }}>
                              Sample Data
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            sx={{
                              p: 0,
                              transition: 'transform 0.2s',
                              transform: sampleOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                            }}
                          >
                            <ExpandMoreIcon sx={{ fontSize: 16, color: '#1f2937' }} />
                          </IconButton>
                        </Box>

                        {/* Collapsible sample rows */}
                        <Collapse in={sampleOpen} timeout={220}>
                          <Box
                            sx={{
                              mt: 0.75,
                              bgcolor: '#ffffff',
                              borderRadius: 1,
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            {target.sampleRows.slice(0, 2).map((row, rowIdx) => {
                              const rowKey = `${sourceKey}-row-${rowIdx}`;
                              const entries = Object.entries(row);
                              const rowOpen = !!rowExpandedMap[rowKey];

                              const PREVIEW_COUNT = 3;
                              const visibleEntries = rowOpen ? entries : entries.slice(0, PREVIEW_COUNT);
                              const hiddenCount = entries.length - PREVIEW_COUNT;
                              const hasMore = entries.length > PREVIEW_COUNT;

                              return (
                                <Box
                                  key={rowKey}
                                  sx={{
                                    p: 1,
                                    borderBottom:
                                      rowIdx < target.sampleRows.slice(0, 2).length - 1 ? '1px dashed #e5e7eb' : 'none',
                                  }}
                                >
                                  <Box
                                    onClick={(e) => hasMore && toggleRowExpanded(e, rowKey)}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      mb: 0.75,
                                      cursor: hasMore ? 'pointer' : 'default',
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.6rem',
                                        color: '#9ca3af',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      Row {rowIdx + 1}
                                    </Typography>

                                    {hasMore && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.25,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.6rem',
                                            color: '#6b7280',
                                            fontStyle: 'italic',
                                          }}
                                        >
                                          {rowOpen ? 'show less' : `+${hiddenCount} more`}
                                        </Typography>
                                        <IconButton
                                          size="small"
                                          sx={{
                                            p: 0,
                                            ml: 0.25,
                                            transition: 'transform 0.2s',
                                            transform: rowOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                          }}
                                        >
                                          <ExpandMoreIcon sx={{ fontSize: 13, color: '#6b7280' }} />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>

                                  {visibleEntries.map(([key, val], colIdx) => {
                                    const colKey = `${rowKey}-col-${colIdx}-${key}`;
                                    return (
                                      <Box key={colKey} sx={{ display: 'flex', mb: 0.5, fontFamily: 'monospace' }}>
                                        <Tooltip title={key}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: '#374151',
                                              width: 90,
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              flexShrink: 0,
                                              fontSize: '0.65rem',
                                            }}
                                          >
                                            {truncateText(key, 12)}:
                                          </Typography>
                                        </Tooltip>
                                        <Tooltip title={val ? String(val) : ''}>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: '#374151',
                                              ml: 1,
                                              wordBreak: 'break-word',
                                              flex: 1,
                                              fontSize: '0.65rem',
                                            }}
                                          >
                                            {renderValue(val, sourceKey, rowIdx, colKey)}
                                          </Typography>
                                        </Tooltip>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    )}

                    {/* Error message */}
                    {target.error && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#c62828' }}>
                          ❌ {target.error}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })
        ) : (
          <Paper
            elevation={0}
            sx={{
              bgcolor: '#f8fafc',
              border: '1px dashed #94a3b8',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <ViewColumnIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ color: '#475569', fontWeight: 600, mb: 1 }}>
              No target connected
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Connect a target block to view schema columns here
            </Typography>
          </Paper>
        )}
      </Box>

      <ProfilingDialog
        open={profilingDialogOpen}
        onClose={() => setProfilingDialogOpen(false)}
        profileData={selectedProfileData}
        sourceName={selectedTargetName}
        totalSampleRows={targetSchema[0]?.sampleRows?.length || 10}
      />
    </Paper>
  );
};

export default TargetPanel;